import React, { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { auth } from '../firebase/config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { useDispatch } from 'react-redux';
import { setUserFacilities } from '../redux/slices/facilitiesSlice';
import { fetchUserFacilities } from '../redux/thunks/facilitiesThunks';
import { getUserProfile } from '../services/userService';
// Removed cacheService import - using Redux only
import { clearMacrosCache } from '../services/macroService';
import requestDeduplicationService from '../services/requestDeduplicationService';

// Global flag to prevent multiple AuthContext instances from interfering
let globalAuthProcessing = false;

const AuthContext = createContext({
  user: null,
  loading: true,
  error: null,
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authInitializedRef = useRef(false);

  // Memoize user profile function to prevent recreating on every render
  const getUserProfileFunction = useMemo(() => {
    console.log('AuthContext: getUserProfileFunction memoized/recreated');
    return getUserProfile;
  }, []); // Empty deps to ensure it's only created once

  // Stable logout function
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      // User state will be automatically updated by onAuthStateChanged
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  }, []);

  // Cleanup function for clearing caches
  const clearCaches = useCallback(() => {
    console.log('Clearing caches');
    // Clear Redux state instead of cache service
    dispatch(setUserFacilities([]));
    clearMacrosCache();
    requestDeduplicationService.clearAllCaches();
    sessionStorage.clear();
  }, [dispatch]);

  // Main authentication effect - Firebase-only
  useEffect(() => {
    console.log('AuthContext: Setting up onAuthStateChanged listener');
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Prevent multiple concurrent processing
      if (globalAuthProcessing) {
        console.log('AuthContext: Skipping concurrent auth processing');
        return;
      }

      globalAuthProcessing = true;
      console.log('AuthContext: Processing auth state change', { 
        uid: firebaseUser?.uid || 'null',
        email: firebaseUser?.email || 'null',
        timestamp: new Date().toISOString()
      });

      try {
        if (firebaseUser) {
          // Only process if not already initialized for this user
          if (authInitializedRef.current === firebaseUser.uid) {
            console.log('AuthContext: User already initialized, skipping');
            globalAuthProcessing = false;
            return;
          }

          authInitializedRef.current = firebaseUser.uid;
          console.log('AuthContext: User authenticated, loading profile');
          
          // Get user profile
          const profile = await getUserProfileFunction(firebaseUser.uid);
          console.log('AuthContext: getUserProfile result:', profile);
          
          if (profile) {
            // Create complete user object with Firebase user + profile data
            const completeUser = {
              uid: firebaseUser.uid,
              email: firebaseUser.email,
              name: profile.name || '',
              defaultFacility: profile.defaultFacility, // Complete facility object
              roles: profile.roles || [profile.role].filter(Boolean),
              role: profile.role || '',
              emailVerified: firebaseUser.emailVerified,
              lastSignInTime: firebaseUser.metadata?.lastSignInTime,
              creationTime: firebaseUser.metadata?.creationTime,
            };
            
            // Set user in local state
            setUser(completeUser);
            setError(null);
            
            // Fetch user facilities for Redux (still needed for facility management)
            console.log('AuthContext: Fetching user facilities');
            try {
              // For admin users, fetch all facilities. For others, fetch only assigned facilities
              let result;
              if (profile.role === 'admin' || profile.roles?.includes('admin')) {
                console.log('AuthContext: Admin user detected, fetching all facilities');
                const { fetchFacilities } = await import('../redux/thunks/facilitiesThunks');
                result = await dispatch(fetchFacilities());
              } else {
                console.log('AuthContext: Regular user detected, fetching assigned facilities');
                result = await dispatch(fetchUserFacilities({ page: 1, limit: 1000 }));
              }
              
              if (result.meta.requestStatus === 'rejected') {
                console.error('AuthContext: Failed to load facilities:', result.error);
              } else {
                console.log('AuthContext: Facilities loaded successfully');
                
                // If user has no default facility, set one from their assigned facilities
                if (!profile.defaultFacility) {
                  const state = result.payload || {};
                  const userFacilities = state.userFacilities || [];
                  if (userFacilities.length > 0) {
                    const fallbackFacility = userFacilities[0]; // Complete facility object
                    console.log('AuthContext: Setting fallback default facility:', fallbackFacility.id);
                    
                    // Update user with fallback facility
                    const updatedUser = {
                      ...completeUser,
                      defaultFacility: fallbackFacility,
                    };
                    setUser(updatedUser);
                    
                    // Update the user's profile in Firestore to persist the default facility ID
                    try {
                      const { updateUserProfile } = await import('../services/userService');
                      await updateUserProfile(firebaseUser.uid, { defaultFacility: fallbackFacility.id });
                      console.log('AuthContext: Updated user profile with fallback default facility');
                    } catch (error) {
                      console.error('AuthContext: Failed to update user profile with fallback facility:', error);
                    }
                  }
                }
              }
            } catch (facilityError) {
              console.error('AuthContext: Error fetching user facilities:', facilityError);
              // Don't fail the entire auth process if facilities fail
            }
            
            console.log('AuthContext: User authentication complete');
          } else {
            console.error('AuthContext: Failed to get user profile');
            setError('Failed to load user profile');
            setUser(null);
          }
        } else {
          console.log('AuthContext: User signed out, clearing state');
          authInitializedRef.current = null;
          setUser(null);
          setError(null);
          clearCaches();
        }
      } catch (error) {
        console.error('AuthContext: Error processing auth state change:', error);
        setError(error.message);
        setUser(null);
        clearCaches();
      } finally {
        setLoading(false);
        globalAuthProcessing = false;
      }
    });
    
    return () => {
      console.log('AuthContext: Cleanup - unsubscribing from auth state changes');
      unsubscribe();
      authInitializedRef.current = null;
      globalAuthProcessing = false;
    };
  }, [dispatch, getUserProfileFunction, clearCaches]);

  // Listen for default facility changes from useFacilitySwitching hook
  useEffect(() => {
    const handleFacilityChange = (event) => {
      const { facilityId, newFacility } = event.detail;
      console.log('AuthContext: Received defaultFacilityChanged event', facilityId);
      
      if (user && facilityId) {
        // If we have the complete facility object, use it; otherwise just update the ID
        setUser(prevUser => ({
          ...prevUser,
          defaultFacility: newFacility || { ...prevUser.defaultFacility, id: facilityId }
        }));
      }
    };

    window.addEventListener('defaultFacilityChanged', handleFacilityChange);
    return () => {
      window.removeEventListener('defaultFacilityChanged', handleFacilityChange);
    };
  }, [user]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    user,
    loading,
    error,
    logout
  }), [user, loading, error, logout]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};